import Fuse from 'fuse.js';

class PageList {
    constructor(element) {
        this.element = element;
        const pageListData = window.pageList[element.id];
        this.pages = pageListData.pages;
        this.categories = pageListData.categories;
        this.filters = {
            categories: new Set(),
            search: ''
        };

        this.pageType = element.dataset.pageType || 'news';

        const { limit } = element.dataset;
        if (limit) {
            this.displayLimit = parseInt(limit, 10);
        }
        if (!this.displayLimit) this.displayLimit = null;

        this.page = 1;

        this.fuse = new Fuse(this.pages, {
            keys: this.getSearchKeys(),
            ignoreLocation: true,
            useExtendedSearch: true
        });

        this.pagesContainer = this.element.querySelector('.page-list-pages');

        this.selectedCategoriesElements = this.element.querySelectorAll(
            '.page-list-selected-category'
        );

        this.categoriesSelect = this.element.querySelector(
            '.page-list-categories'
        );

        this.searchInput = this.element.querySelector('.page-list-search');

        if (window.category) {
            this.filters.categories.add(window.category.id);
        }
        this.loadMore = this.element.querySelector('.page-list-load-more');
        this.count = this.element.querySelector('.page-list-count');
        this.searchTimeout = null;
    }

    getSearchKeys() {
        if (this.pageType === 'news') {
            return [
                {
                    name: 'newsDate',
                    weight: 3
                },
                {
                    name: 'newsTitle',
                    weight: 3
                },
                {
                    name: 'name',
                    weight: 3
                },
                {
                    name: 'newsCategoriesString',
                    weight: 2
                },
                {
                    name: 'newsDescription',
                    weight: 2
                },
                {
                    name: 'newsAuthor',
                    weight: 2
                },
                {
                    name: 'indexedContent',
                    weight: 1
                }
            ];
        } else if (this.pageType === 'webinar') {
            return [
                {
                    name: 'webinarStartDatetime',
                    weight: 3
                },
                {
                    name: 'webinarEndDatetime',
                    weight: 3
                },
                {
                    name: 'webinarTitle',
                    weight: 3
                },
                {
                    name: 'newsCategories',
                    weight: 2
                },
                {
                    name: 'webinarDescription',
                    weight: 2
                },
                {
                    name: 'indexedContent',
                    weight: 1
                }
            ];
        }
    }

    init() {
        this.bindEvents();
        this.display();
    }

    getMaximumVisibleElements() {
        if (!this.displayLimit) return this.pages.length;
        return Math.min(this.pages.length, this.displayLimit * this.page);
    }

    updateLoadMoreButton() {
        this.loadMore.style.display =
            this.getMaximumVisibleElements() < this.pages.length
                ? 'inline-block'
                : 'none';
    }

    updateDisplayCount(displayed, total) {
        this.count.textContent = `${displayed} of ${total} results`;
    }

    bindEvents() {
        this.categoriesSelect.addEventListener(
            'change',
            this.onCategoryChange.bind(this)
        );
        this.searchInput.addEventListener(
            'input',
            this.onSearchChange.bind(this)
        );
        this.element
            .querySelectorAll('.page-list-selected-category button')
            .forEach((button) => {
                button.addEventListener(
                    'click',
                    this.onSelectedCategoryClick.bind(this)
                );
            });

        this.loadMore.addEventListener('click', this.onLoadMore.bind(this));
    }

    onSelectedCategoryClick(event) {
        const { category } = event.currentTarget.parentElement.dataset;
        if (category) {
            this.filters.categories.delete(category);
            this.displaySelectedCategories();
            this.display();
        }
    }

    displaySelectedCategories() {
        this.selectedCategoriesElements.forEach((element) => {
            const name = element.dataset.category;
            if (name && this.filters.categories.has(name)) {
                // eslint-disable-next-line no-param-reassign
                element.style.display = 'block';
            } else {
                // eslint-disable-next-line no-param-reassign
                element.style.display = 'none';
            }
        });
        this.categoriesSelect.querySelectorAll('option').forEach((option) => {
            if (this.filters.categories.has(option.value)) {
                // eslint-disable-next-line no-param-reassign
                option.style.display = 'none';
            } else {
                // eslint-disable-next-line no-param-reassign
                option.style.display = 'block';
            }
        });
    }

    onLoadMore() {
        this.page += 1;
        this.display();
    }

    onCategoryChange(event) {
        const { options } = event.target;
        this.page = 1;
        Array.from(options).forEach((option) => {
            if (option.selected && option.value !== '') {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'news_category',
                    filterCategory: option.value
                });
                this.filters.categories.add(option.value);
            }
        });
        this.categoriesSelect.value = '';
        this.displaySelectedCategories();
        this.display();
    }

    onCategoryFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const categoryParam = urlParams.get('category');

        if (categoryParam) {
            this.filters.categories.add(categoryParam);

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'news_category',
                filterCategory: categoryParam
            });

            this.displaySelectedCategories();
            this.display();
        }
    }

    onSearchChange(event) {
        this.filters.search = event.target.value;
        this.page = 1;
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            if (this.filters.search) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'news_search',
                    filterSearch: this.filters.search
                });
            }
        }, 1000);
        this.display();
    }

    filterData() {
        let entries = this.filterBySearch();
        entries = this.filterByCategories(entries);
        return entries;
    }


    filterBySearch() {
        if (!this.filters.search) return this.pages;
        return this.fuse
            .search(`'${this.filters.search}`)
            .map((result) => result.item);
    }

    filterByCategories(entries) {
        if (!this.filters.categories.size) return entries;

        return entries.filter((entry) => {
            if (this.pageType === 'webinar') {
                return Object.keys(entry.webinarCategories).some((category) => this.filters.categories.has(category));
            }
            return Object.keys(entry.newsCategories).some((category) => this.filters.categories.has(category));
        });
    }

    limitData(elements) {
        return elements.slice(0, this.getMaximumVisibleElements());
    }

    getDisplayIdsSet(elements) {
        const displayIds = new Set();
        for (let i = 0; i < elements.length; i += 1) {
            displayIds.add(`${this.element.id}-${elements[i].pageId}`);
        }
        return displayIds;
    }

    display() {
        const allAfterFilter = this.filterData();

        const displayIds = this.getDisplayIdsSet(
            this.limitData(allAfterFilter)
        );

        const pages = this.pagesContainer.children;
        for (let i = 0; i < pages.length; i += 1) {
            const page = pages[i];
            const { id } = page;
            if (displayIds.has(id)) {
                page.style.display = 'flex';
            } else {
                page.style.display = 'none';
            }
        }

        // If in displayData, display. If not, keep hidden.

        this.updateLoadMoreButton();
        this.updateDisplayCount(
            Math.min(displayIds.size, this.getMaximumVisibleElements()),
            allAfterFilter.length
        );
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const pageLists = document.querySelectorAll('.page-list');
    window.pageListObjects = [];
    pageLists.forEach((pageList) => {
        const pl = new PageList(pageList);
        pl.init();
        window.pageListObjects.push(pl);
        pl.onCategoryFromUrl();
    });
});
